import { useEffect, useRef } from 'react';

/**
 * Announces route changes to screen readers via a visually hidden live region.
 * Place this component once at the app root level.
 */
export function RouteAnnouncer({ pathname, appName }: { pathname: string; appName: string }) {
  const announcerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Build a human-readable page title from the pathname
    const segments = pathname.split('/').filter(Boolean);
    const pageTitle =
      segments.length === 0
        ? 'Dashboard'
        : segments
            .map((s) =>
              s
                .replace(/-/g, ' ')
                .replace(/\b\w/g, (c) => c.toUpperCase()),
            )
            .join(' - ');

    const fullTitle = `${pageTitle} | ${appName}`;
    document.title = fullTitle;

    // Announce to screen readers after a short delay to allow DOM to settle
    const timer = setTimeout(() => {
      if (announcerRef.current) {
        announcerRef.current.textContent = `Navigated to ${pageTitle}`;
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [pathname, appName]);

  return (
    <div
      ref={announcerRef}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      style={{
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0,
      }}
    />
  );
}
